// Mirrored in frontend/src/utils/temporaryPriceCore.js; parity is verified by tests.
// Prices are whole pesos. Packs remain exact totals, never rounded unit prices.
const number = (value, fallback = 0) => {
    if (value === null || value === undefined || value === '') return fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};
const nonnegative = (value) => Math.max(number(value), 0);
const isValidTemporaryPriceQuantity = (type, quantity) => {
    if (typeof quantity !== 'number' || !Number.isFinite(quantity) || quantity <= 0 || quantity > 2147483647) return false;
    if (type === 'weight_threshold') return Math.abs(quantity * 1000 - Math.round(quantity * 1000)) < 0.00001;
    return Number.isSafeInteger(quantity) && (type === 'unit_price' ? quantity === 1 : type === 'pack' && quantity >= 2);
};
// Kilogram prices and scale inputs use quantities expressed in kilograms.
// Grams and volume need their own conversion and are not eligible here.
const isKilogramTemporaryPriceItem = (item) => {
    const unit = String(item.metricUnit || '').trim().toLowerCase();
    return ['kg', 'kilo', 'kilos', 'kilogramo', 'kilogramos'].includes(unit)
        || item.useScaleWeight === true;
};
const normalizeTemporaryPrice = (record) => {
    const row = record?.toJSON ? record.toJSON() : record;
    if (!row) return null;
    const type = row.promotionType || row.type;
    const price = number(row.sellingPrice ?? row.promotionPrice, -1);
    const quantity = number(row.quantity);
    if (!['unit_price', 'pack', 'weight_threshold'].includes(type) || !Number.isSafeInteger(price) || price <= 0
        || !isValidTemporaryPriceQuantity(type, quantity)
        || !Number.isSafeInteger(number(row.varietyPriceId)) || number(row.varietyPriceId) <= 0) return null;
    return { ...row, id: number(row.id), type, promotionPrice: price, quantity };
};
const activeTemporaryPrices = (records = [], now = Date.now()) => records
    .map(normalizeTemporaryPrice)
    .filter((row) => row && !row.endedAt && !row.endedReason
        && (row.maxQuantity == null || number(row.soldQuantity) < number(row.maxQuantity) - number(row.tolerance))
        && !['expired', 'ended', 'cancelled', 'replaced', 'imported', 'archived'].includes(row.state)
        && (!row.startsAt || new Date(row.startsAt).getTime() <= Number(new Date(now)))
        && (!row.expiresAt || new Date(row.expiresAt).getTime() > Number(new Date(now))));
const resolveTemporaryUnitPrice = (regularPrice, records = [], now = Date.now()) => {
    const unitPrice = activeTemporaryPrices(records, now)
        .filter((row) => row.type === 'unit_price')
        .sort((a, b) => (number(Date.parse(b.createdAt)) - number(Date.parse(a.createdAt))) || b.id - a.id)[0] || null;
    return { regularPrice, effectivePrice: unitPrice ? unitPrice.promotionPrice : regularPrice, unitPrice };
};
const isDiscreteTemporaryPriceItem = (item) => {
    if (item.useScaleWeight || (item.quantityType && item.quantityType !== 'unit')) return false;
    const unit = String(item.metricUnit || '').trim().toLowerCase();
    return !['kg', 'kilo', 'gr', 'lt', 'litro'].some((token) => unit.includes(token))
        && !['g', 'l'].includes(unit) && Number.isInteger(Number(item.quantity));
};
// Decimal multiplication without floating-point rounding at peso boundaries.
const fraction = (value) => {
    const text = number(value, -1) >= 0 ? String(value).trim() : '0';
    const [mantissa, exponent = '0'] = text.toLowerCase().split('e');
    const [integer, decimal = ''] = mantissa.split('.');
    const scale = decimal.length - Number(exponent);
    const numerator = BigInt(integer + decimal);
    const powerOfTen = BigInt(`1${'0'.repeat(Math.abs(scale))}`);
    return scale >= 0 ? [numerator, powerOfTen] : [numerator * powerOfTen, 1n];
};
const floorProduct = (amount, value, divisor = 1) => {
    const [a, ad] = fraction(amount);
    const [b, bd] = fraction(value);
    const [d, dd] = fraction(divisor);
    return Number(a * b * dd / (ad * bd * d));
};
const segmentCategoryDiscount = (item, price, quantity) => {
    if (item.clientPricingMarginReductionPercent != null) {
        if (item.clientPricingReferenceCost == null) return 0;
        const [cost, denominator] = fraction(item.clientPricingReferenceCost);
        const [count, countDenominator] = fraction(quantity);
        const margin = BigInt(price) * denominator * countDenominator - cost * count;
        const [rate, rateDenominator] = fraction(item.clientPricingMarginReductionPercent);
        return margin > 0n ? Number(margin * rate / (denominator * countDenominator * rateDenominator * 100n)) : 0;
    }
    if (item.clientPricingDiscountType === 'percent') {
        return floorProduct(price, item.clientPricingDiscountValue, 100);
    }
    if (item.clientPricingDiscountType === 'amount') {
        return Math.floor(nonnegative(item.clientPricingDiscountValue)) * quantity;
    }
    // Compatibility with callers carrying only an already resolved unit discount.
    return item.discountSource === 'client_category' ? Math.floor(nonnegative(item.discount)) * quantity : 0;
};
const selectSegmentCategoryRule = (item, price, quantity) => {
    if (!item.clientPricingCandidateRules?.length) return item;
    return item.clientPricingCandidateRules.map((rule) => ({
        ...item,
        clientPricingCategoryId: rule.clientPricingCategoryId,
        clientPricingCategoryName: rule.clientPricingCategoryName,
        clientPricingDiscountType: rule.discountType,
        clientPricingDiscountValue: rule.discountValue,
        clientPricingRuleType: rule.ruleType || (rule.marginReductionPercent != null ? 'margin_discount' : 'fixed_discount'),
        clientPricingMarginReductionPercent: rule.marginReductionPercent ?? null,
        clientPricingReferenceCost: rule.referenceCost?.referenceCostRaw ?? rule.referenceCost?.referenceCost
            ?? rule.referenceCostValue ?? item.clientPricingReferenceCost,
    })).sort((a, b) => Math.min(price, segmentCategoryDiscount(b, price, quantity)) - Math.min(price, segmentCategoryDiscount(a, price, quantity))
        || Number(a.clientPricingCategoryId) - Number(b.clientPricingCategoryId))[0];
};
const priceSegment = (item, price, quantity, count) => {
    const automaticSource = ['client_category', 'promotion'].includes(item.discountSource);
    const manualAmount = automaticSource ? item.manualDiscount : item.discount ?? item.manualDiscount;
    const manual = Math.floor(nonnegative(manualAmount)) * quantity;
    const selected = selectSegmentCategoryRule(item, price, quantity);
    const category = Math.min(price, segmentCategoryDiscount(selected, price, quantity));
    const selection = item.clientPricingCandidateRules?.length ? {
        clientPricingCategoryId: selected.clientPricingCategoryId,
        clientPricingCategoryName: selected.clientPricingCategoryName,
        clientPricingRuleType: selected.clientPricingRuleType,
        clientPricingDiscountType: selected.clientPricingDiscountType,
        clientPricingDiscountValue: selected.clientPricingDiscountValue,
        clientPricingMarginReductionPercent: selected.clientPricingMarginReductionPercent,
        clientPricingReferenceCost: selected.clientPricingReferenceCost,
    } : {};
    const discount = Math.min(price, Math.max(manual, category));
    return { ...selection, quantity, count, price, discount, categoryDiscount: category,
        discountSource: discount === 0 ? 'none' : category >= manual ? 'client_category' : 'manual',
        subtotal: Math.max(Math.floor((price - discount) * count), 0) };
};
const weightPromotionKey = (promotion) => `${promotion.varietyPriceId}:${promotion.id}:${promotion.quantity}:${promotion.promotionPrice}`;
const sumWeight = (left, right) => Number((left + right).toFixed(9));
const calculateTemporaryPriceLine = (item, records = [], now = Date.now(), weightContext = null) => {
    const applicable = activeTemporaryPrices(records, now)
        .filter((row) => Number(row.varietyPriceId) === Number(item.varietyPriceId));
    const { regularPrice, effectivePrice, unitPrice } = resolveTemporaryUnitPrice(
        number(item.regularPrice ?? item.price), applicable, now
    );
    const quantity = nonnegative(item.quantity);
    const ordinarySubtotal = Math.floor(effectivePrice * quantity);
    let winner = { pack: null, packs: 0, remainder: quantity, subtotal: ordinarySubtotal };
    if (isDiscreteTemporaryPriceItem(item)) {
        applicable.filter((row) => row.type === 'pack').sort((a, b) => a.id - b.id).forEach((pack) => {
            const packs = Math.floor(quantity / pack.quantity);
            const remainder = quantity % pack.quantity;
            const subtotal = packs * pack.promotionPrice + remainder * effectivePrice;
            if (packs > 0 && subtotal < winner.subtotal) winner = { pack, packs, remainder, subtotal };
        });
    }
    if (isKilogramTemporaryPriceItem(item)) {
        applicable.filter((row) => row.type === 'weight_threshold').sort((a, b) => a.id - b.id).forEach((weight) => {
            const qualifyingWeight = weightContext?.quantities.get(weightPromotionKey(weight)) ?? quantity;
            if (qualifyingWeight < weight.quantity) return;
            if (weightContext?.allocation && weightContext.allocation.id !== weight.id) return;
            // Multiply the exact promotion total first; never round the price/kg.
            const subtotal = weightContext?.allocation?.subtotal ?? floorProduct(weight.promotionPrice, quantity, weight.quantity);
            if (weightContext?.allocation || subtotal < winner.subtotal) winner = { weight, qualifyingWeight, pack: null, packs: 0, remainder: 0, subtotal };
        });
    }
    const segments = [];
    if (winner.weight) segments.push(priceSegment(item, winner.subtotal, quantity, 1));
    if (winner.pack) segments.push(priceSegment(item, winner.pack.promotionPrice, winner.pack.quantity, winner.packs));
    if (winner.remainder > 0) segments.push(priceSegment(item, effectivePrice, 1, winner.remainder));
    const subtotal = segments.reduce((sum, segment) => sum + segment.subtotal, 0);
    const unitSegment = priceSegment(item, effectivePrice, 1, 1);
    const applied = winner.weight || winner.pack || unitPrice;
    const snapshot = applied ? {
        kind: 'temporary_price', version: 1,
        unitPriceId: unitPrice?.id || null, packPriceId: winner.pack?.id || null,
        regularPrice, effectiveUnitPrice: effectivePrice,
        ...(winner.weight ? { weightPriceId: winner.weight.id, minimumWeight: winner.weight.quantity,
            weightPromotionPrice: winner.weight.promotionPrice, promotedWeight: quantity, qualifyingWeight: winner.qualifyingWeight,
            weightUnitPrice: winner.weight.promotionPrice / winner.weight.quantity } : {}),
        baseSubtotal: winner.subtotal, discountAmount: winner.subtotal - subtotal,
        packQuantity: winner.pack?.quantity || null, packCount: winner.packs,
        remainderQuantity: winner.remainder, segments,
    } : null;
    const selectedUnitRule = selectSegmentCategoryRule(item, effectivePrice, 1);
    return { ...selectedUnitRule, regularPrice, effectivePrice, price: effectivePrice,
        discount: unitSegment.discount,
        discountSource: unitSegment.discountSource,
        promotionId: applied?.id || null, promotionName: applied ? (winner.weight ? `${winner.weight.quantity.toLocaleString('es-CL')} kg por $${winner.weight.promotionPrice.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} · precio/kg desde ese peso` : winner.pack ? `${winner.pack.quantity} por $${winner.pack.promotionPrice}` : 'Precio temporal') : null,
        promotionType: applied?.type || null, promotionDiscountAmount: 0,
        promotionSnapshot: snapshot, subtotal,
        orderDiscountEligibleSubtotal: subtotal, promotionProtectedSubtotal: 0 };
};
// Entries carry server-verified or locally captured price contexts. Only lines
// sharing an active immutable rule and format count toward the same threshold.
const calculateTemporaryPriceLines = (entries = []) => {
    const quantities = new Map();
    const available = entries.map(({ item, records = [], now, skip }) => {
        if (skip || !isKilogramTemporaryPriceItem(item)) return [];
        const seen = new Set();
        return activeTemporaryPrices(records, now).filter((row) => {
            const key = weightPromotionKey(row);
            if (row.type !== 'weight_threshold' || Number(row.varietyPriceId) !== Number(item.varietyPriceId) || seen.has(key)) return false;
            seen.add(key);
            quantities.set(key, sumWeight(quantities.get(key) || 0, nonnegative(item.quantity)));
            return true;
        });
    });
    const cumulative = new Map();
    return entries.map(({ item, records = [], now, skip }, index) => {
        if (skip) return item;
        const result = calculateTemporaryPriceLine(item, records, now, { quantities });
        const weight = available[index].find((row) => row.id === result.promotionSnapshot?.weightPriceId);
        if (!weight) return result;
        const key = weightPromotionKey(weight);
        const previous = cumulative.get(key) || 0;
        const next = sumWeight(previous, nonnegative(item.quantity));
        cumulative.set(key, next);
        // Allocate whole pesos across readings so 2 kg + 1 kg totals exactly
        // the same as a single 3 kg reading, including non-divisible prices.
        const subtotal = floorProduct(weight.promotionPrice, next, weight.quantity)
            - floorProduct(weight.promotionPrice, previous, weight.quantity);
        return calculateTemporaryPriceLine(item, records, now, { quantities, allocation: { id: weight.id, subtotal } });
    });
};
const getTemporaryPriceTotals = (item) => {
    const snapshot = item?.promotionSnapshot;
    return snapshot?.kind === 'temporary_price'
        ? { baseSubtotal: nonnegative(snapshot.baseSubtotal), discountAmount: nonnegative(snapshot.discountAmount) }
        : null;
};
export {
    getTemporaryPriceTotals, normalizeTemporaryPrice, activeTemporaryPrices, resolveTemporaryUnitPrice,
    isDiscreteTemporaryPriceItem, isKilogramTemporaryPriceItem, isValidTemporaryPriceQuantity, calculateTemporaryPriceLine, calculateTemporaryPriceLines,
};
